import React from "react";
import autoBind from "react-autobind";
import {Link} from "react-router-dom";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import Avatar from "@material-ui/core/Avatar";
import Divider from "@material-ui/core/Divider";
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Table from "../../../components/Table";
import TableHeader from "../../../components/Table/TableHeader";
import TableHeaderColumn from "../../../components/Table/TableHeaderColumn";
import TableRow from "../../../components/Table/TableRow";
import TableRowColumn from "../../../components/Table/TableRowColumn";
import TableSearch from "../../../components/Table/TableSearch";
import FilterBank from "../../../components/FilterBank";
import FilterCheck from "../../../components/FilterCheck";
import FieldToggle from "../../../components/FieldToggle";
import {UserRecord, InvitationRecord, PermissionRecord} from "../../../feathers/records";
import feathers from "../../../feathers";
import PaginationBtns from "../../../components/PaginationBtns";
import WithRecords from "../../../components/WithRecords";
import "./style.scss";

const UserAvatar = ({src}) => <Avatar src={src || "/img/default-profile.png"} style={{marginRight: 15}} />;

class Users extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      fetching: false,
      useFilter: false,
      selectedFilterTab: 0,
      filter: {},
      groupQuery: {},
      options: {
        facilities: undefined,
        buildings: undefined,
        markerTypeCategories: undefined,
        groups: undefined
      }
    };
    this.searchFields = ["first_name", "last_name", "email"];
    this.editItems = [
      {_id: "account", name: "Account"},
      {_id: "resources", name: "Resources"},
      {_id: "users", name: "Users"}
    ];
    this.groupsService = feathers.service("api/groups");
    autoBind(this);
  }

  async componentDidMount() {
    // The users/invitations are already in state, but refresh them
    // from the server anytime someone comes to the
    // /users page. This will also ensure the latest
    // at the /users/123 page as well.
    UserRecord.find();
    InvitationRecord.find();
    PermissionRecord.find();
    const options = await feathers.service("api/permissions-options").find();
    this.setState({options});
  }

  handleSearchSubmit = async () => {
    if (this.props.currentQuery.$or) {
      if (this.props.currentQuery.$or[0][this.searchFields[0]]) {
        const searchString = this.props.currentQuery.$or[0][this.searchFields[0]].$regex;
        // for now, enforcing an exact match search on categories. Searching based
        // on the props rather than a database call for the first part
        const searchRegex = new RegExp("^" + searchString + "$");
        var searchResult = this.state.options.groups.filter(obj => {
          return searchRegex.test(obj.name);
        });
        const groupIDs = searchResult.map(group => {
          return group["_id"];
        });

        var newQuery = {...this.props.currentQuery};
        if (groupIDs.length > 0) {
          const result = await PermissionRecord.find({
            query: {
              "permissions.groups": {$in: groupIDs}
            },
            skipHooks: "setListInState"
          });
          const userIDs = result.data.toList().map(permission => {
            return permission["user_id"];
          });
          newQuery.$or.push({_id: {$in: userIDs}});
        }

        return this.props.goToPage(1, newQuery);
      }
    }
    this.props.onSearchSubmit();
  };

  renderFacilitiesFilter = props => {
    const {path, filter} = props;
    const {options, fetching} = this.state;
    return (
      <React.Fragment>
        {options.facilities.map(facility => {
          const allChecked =
            filter[path] && facility.buildings.every(building => filter[path].$all.find(_id => _id === building._id));
          return (
            <React.Fragment>
              <FieldToggle
                label={facility.name}
                disabled={fetching}
                value={allChecked}
                onChange={() => {
                  var newFilter = {...filter};
                  if (allChecked) {
                    delete newFilter[path];
                    this.handleFilterChange({filter: newFilter});
                  } else {
                    newFilter[path] = {$all: []};
                    newFilter[path].$all = facility.buildings.map(building => {
                      return building._id;
                    });
                    this.handleFilterChange({filter: newFilter});
                  }
                }}
              />
              <div div className="filterBank">
                {facility.buildings.map(building => (
                  <FilterCheck
                    key={building._id}
                    {...props}
                    name={building._id}
                    label={building.name}
                    disabled={fetching}
                  />
                ))}
              </div>
            </React.Fragment>
          );
        })}
      </React.Fragment>
    );
  };

  renderGroups(record) {
    const {options} = this.state;
    if (record && record.permissions.groups && options.groups) {
      return record.permissions.groups.map((groupID, index) => {
        const groupItem = options.groups.find(group => {
          return group._id === groupID;
        });
        if (groupItem) {
          return <span key={index}>{index > 0 ? `, ${groupItem.name}` : groupItem.name}</span>;
        }
        return null;
      });
    }
    return null;
  }

  renderUsersTable({users, invitations, $sort}) {
    const {onSort, currentUser} = this.props;
    return (
      <Table hover>
        <TableHeader>
          <TableHeaderColumn
            className="col-xs-12 col-md-5 sortable-column"
            sortName="last_name"
            sort={$sort.last_name}
            onClick={onSort}>
            Name
          </TableHeaderColumn>
          <TableHeaderColumn
            className="col-xs-12 col-md-4 sortable-column"
            sortName="email"
            sort={$sort.email}
            onClick={onSort}>
            Email
          </TableHeaderColumn>
          <TableHeaderColumn className="col-xs-12 col-md-3">
            Groups&nbsp;
            <Link to="/account/groups" className="small-link">
              [ View ]
            </Link>
          </TableHeaderColumn>
        </TableHeader>
        {users.toList().map(user => {
          const userPermissions = user.getPermissions();
          return (
            <Link to={`/account/users/${user._id}`} key={user._id}>
              <TableRow>
                <TableRowColumn className="col-xs-12 col-md-5">
                  <UserAvatar src={user.image.path} />
                  {user._id === currentUser._id ? `${user.full_name} (you)` : user.full_name}
                </TableRowColumn>
                <TableRowColumn className="col-xs-12 col-md-4">{user.email}</TableRowColumn>
                <TableRowColumn className="break-row col-xs-12 col-md-3">
                  {this.renderGroups(userPermissions)}
                </TableRowColumn>
              </TableRow>
            </Link>
          );
        })}
        {invitations.toList().map(invitation => (
          <Link to={`/account/invitations/${invitation._id}`} key={invitation._id}>
            <TableRow className="text-muted">
              <TableRowColumn className="col-xs-12 col-md-5">
                <UserAvatar src={null} />
                Invitation
              </TableRowColumn>
              <TableRowColumn className="col-xs-12 col-md-4">{invitation.email}</TableRowColumn>
              <TableRowColumn className="break-row col-sm-12 col-md-3">{this.renderGroups(invitation)}</TableRowColumn>
            </TableRow>
          </Link>
        ))}
      </Table>
    );
  }

  handleFilterChange = async ({filter}) => {
    this.setState({filter});
    if (Object.keys(filter).length) {
      const result = await PermissionRecord.find({
        query: filter,
        skipHooks: "setListInState"
      });
      const idList = result.data.toList().map(item => {
        return item["user_id"];
      });
      if (idList.size === 0) {
        // no results
        return this.props.onFilter("zero");
      }
      // wrapped in an $or to avoid the _id being converted on the server
      return this.props.onFilter({
        $or: [
          {
            _id: {
              $in: idList.toArray()
            }
          }
        ]
      });
    } else {
      return this.props.onFilter();
    }
  };

  changeFilterTab = (event, value) => {
    this.setState({selectedFilterTab: value});
  };
  renderFilter() {
    const {options, useFilter, filter, selectedFilterTab} = this.state;

    return (
      <React.Fragment>
        <FieldToggle
          style={{marginTop: "10px"}}
          name="useFilter"
          onChange={({name, value}) => {
            this.setState({useFilter: value});
            if (!value) {
              this.handleFilterChange({filter: {}});
            }
          }}
          label="Filter"
          value={useFilter}
        />
        {useFilter ? (
          <React.Fragment>
            <Tabs
              variant="fullWidth"
              indicatorColor="primary"
              style={{marginTop: "4px", marginBottom: "10px"}}
              contentContainerStyle={{
                overflow: "hidden"
              }}
              value={selectedFilterTab}
              onChange={this.changeFilterTab}>
              <Tab key={"tab_facilities"} label="Facilities" />
              <Tab key={"tab_groups"} label="Groups" />
              <Tab key={"tab_permissions"} label="Edit Permissions" />
            </Tabs>
            {selectedFilterTab === 0 && (
              <FilterBank
                data={options.buildings}
                filter={filter}
                path="permissions.resources.buildings"
                onChange={this.handleFilterChange}
                renderBank={this.renderFacilitiesFilter}
              />
            )}
            {selectedFilterTab === 1 && (
              <FilterBank
                data={options.groups}
                filter={filter}
                path="permissions.groups"
                onChange={this.handleFilterChange}
              />
            )}
            {selectedFilterTab === 2 && (
              <FilterBank
                data={this.editItems}
                filter={filter}
                path="permissions.edit"
                onChange={this.handleFilterChange}
                getValue={({filter, path, name}) => {
                  return filter && filter[path + "." + name];
                }}
                setValue={({filter, path, name, value}) => {
                  if (!(filter && filter[path + "." + name])) {
                    return {...filter, [path + "." + name]: true};
                  } else {
                    delete filter[path + "." + name];
                    return filter;
                  }
                }}
              />
            )}
            <Divider />
          </React.Fragment>
        ) : null}
      </React.Fragment>
    );
  }

  render() {
    const {
      resources: users,
      invitations,
      currentPage,
      goToPage,
      onQueryChange,
      currentQuery: _currentQuery,
      total
    } = this.props;
    const {fetching} = this.state;
    const currentQuery = Object.assign({$sort: {}, ..._currentQuery});
    const {$sort} = currentQuery;
    const searchString =
      currentQuery.$or && currentQuery.$or[0][this.searchFields[0]]
        ? currentQuery.$or[0][this.searchFields[0]].$regex
        : "";
    return (
      <div className="margin-bottom-30">
        <Paper>
          <div className="container-fluid container-paper">
            <div className="row">
              <div className="col-xs-12 col-md-3 margin-bottom-15">
                <Link to="/account/invitations">
                  <Button variant="contained" color="primary">
                    New Invitation
                  </Button>
                </Link>
                <span>&nbsp;</span>
              </div>
              <div className="col-xs-12 col-md-9 margin-bottom-15">
                <TableSearch
                  value={searchString}
                  onSubmit={this.handleSearchSubmit}
                  onQueryChange={onQueryChange}
                  searchFields={this.searchFields}
                  disabled={!searchString}
                />
              </div>
            </div>
            {this.renderFilter()}
            {this.renderUsersTable({users, invitations, $sort})}
            <div className="col-sm-12 gap-row">
              &nbsp;
              <div style={{float: "right"}}>
                {
                  <PaginationBtns
                    pagination={this.props.pagination}
                    totalItems={total}
                    currentPage={currentPage}
                    goToPage={goToPage}
                  />
                }
              </div>
            </div>
          </div>
        </Paper>
        {fetching && <LinearProgress mode="indeterminate" />}
      </div>
    );
  }
}

export default props => {
  const store = props.app.state.get("users");
  const invitations = props.app.state.get("invitations");
  return (
    <WithRecords
      store={store}
      record={UserRecord}
      render={withRecordsProps => {
        return <Users {...props} invitations={invitations} {...withRecordsProps} />;
      }}
    />
  );
};



// WEBPACK FOOTER //
// ./src/pages/Account/Users/index.jsx